/* eslint-disable camelcase */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { reduxForm, SubmissionError, change as changeInput } from 'redux-form';
import { batchActions } from 'redux-batched-actions';
import { connect } from 'react-redux';
import { Navigation } from '../../navigation/react-redux-nav';
import { createUser, checkEmailExists, updateEmailWarning } from '../../store/ducks/user';
import { getFacebookProfileData } from '../../store/ducks/facebook';
import { closeWebview, openTermsOfUse, openPrivacyPolicy, redirectToLogin, openCoppaHelp } from '../../store/ducks/shared';
import { EmailTakenError, FacebookUserAlreadyExists } from '../../errors';
import Icon, { NAMES } from '@ifit/react-icons';
import EmailField from './components/email-field';
import FullNameField from './components/full-name-field';
import PasswordField from '../../components/password-field';
import BirthdayField from './components/birthday-field';
import MarketingEmailsField from './components/marketing-emails-field';
import TermsOfServiceField from './components/terms-of-service-field';
import CountryField from './components/country-field';
import PhoneField from './components/phone-field';
import UnderAgeWarningModal from '../../components/modal';
import FloatingBar from '../../components/floating-bar';
import MuiRaisedButton from 'material-ui/RaisedButton';
import classNames from 'classnames';
import { scienceBlue } from '../../theme/colors';
import { calculateAge } from '../../helpers/birthday';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import MarketingSMSField from './components/marketing-sms-field';

import styles from '../../shared-styles';
import './index.css';

const UNDER_LEGAL_AGE_ERROR = 'You must be at least 13 years old to use iFit.';
const submitButtonStyle = {
  button: {
    ...styles.buttonStyle,
    minWidth: '243px',
    width: '243px',
    margin: '2em auto 20px',
    display: 'block',
  },
  label: {
    ...styles.buttonLabelStyle,
    width: '243px',
    margin: 'auto'
  }
};

export class PureCreateAccountLoginInfoPage extends Component {
  static propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    createUser: PropTypes.func.isRequired,
    onSuccessfulSubmit: PropTypes.func.isRequired,
    showPassword: PropTypes.bool,
    submitting: PropTypes.bool,
    error: PropTypes.string,
    showAgeWarning: PropTypes.bool,
    invalid: PropTypes.bool,
    facebookInfo: PropTypes.object,
    getUserFacebookProfile: PropTypes.func.isRequired,
    closeWebview: PropTypes.func.isRequired,
    appName: PropTypes.string,
    emailWarning: PropTypes.string,
    checkEmailExists: PropTypes.func.isRequired,
    openTermsOfUse: PropTypes.func.isRequired,
    openPrivacyPolicy: PropTypes.func.isRequired,
    redirectToLogin: PropTypes.func.isRequired,
    openCoppaHelp: PropTypes.func.isRequired,
    clearEmailWarning: PropTypes.func.isRequired,
    mediaType: PropTypes.string
  };

  state = {
    facebookError: null,
    countryHint: 'US',
    showSMSCheckbox: false,
    isSelectMenuOpen: false,
    emailFocus: false
  }

  getUserFacebookInfo = async () => {
    try {
      await this.props.getUserFacebookProfile();
    } catch (err) {
      this.setState({ facebookError: err.message });
    }
  }

  validateEmail = (email = '') => {
    const emailRegex = /^[^@^\s]+@[^@^\s]+\.[^@^\s]+$/;

    if (email && !emailRegex.test(email)) {
      return 'Valid email required.';
    }
  }

  parseFullName = (name = '') => {
    return name.trim().split(' ').filter(word => /[a-zA-Z]+/g.test(word));
  }

  validateFullName = (name = '') => {
    name = name.trim();
    const names = this.parseFullName(name);

    if (!name) return 'Valid name required.';
    if (names.length === 1) return 'Please include your first and last name.';
    if (/\btest\b/i.test(name)) return 'Name cannot be set to "Test".';
  }

  validatePassword = (password = '', values) => {
    if (!password) return 'Valid password required.';
    if (password.trim().length < 8) return 'Your password needs to be at least 8 characters.';
    if (values.email) {
      const username = this.getPotentialUsername(values.email);
      if (username === password) return 'Username cannot be used as password.';
    }
  }

  // Source: https://github.com/ifit/ifit/blob/master/models/website/user/user.js#L1300-L1331
  getPotentialUsername = function(email) {
    let potentialUsername = email.split('@')[0]
      .replace(/[^0-9a-zA-Z]/g, '')
      .toLowerCase();

    if (potentialUsername.length > 40) {
      potentialUsername = potentialUsername.substring(0, 35);
    }

    while (potentialUsername.length < 4) {
      potentialUsername += '0';
    }

    return potentialUsername;
  };

  validateBirthday = (birthday) => {
    if (!birthday) return 'Valid birthday required.';
  }

  trackEvent(event, props) {
    if (window.analytics) {
      window.analytics.track(event, props);
    }
  }

  validateTermsOfService = (tos) => {
    if (!tos) return 'Please agree to the terms of service.';
  }

  parsePhoneNumber = (phoneNumber) => {
    let number = parsePhoneNumberFromString(phoneNumber, this.state.countryHint);

    // Number is not valid, but could be an international number
    if ((!number || !number.isValid()) && !phoneNumber.includes('+')) {
      number = parsePhoneNumberFromString('+' + phoneNumber, this.state.countryHint);
    }

    // Number is still not valid, try as US number
    if (this.state.countryHint !== 'US' && (!number || !number.isValid()) && !phoneNumber.includes('+')) {
      number = parsePhoneNumberFromString('+1' + phoneNumber, this.state.countryHint);
    }

    (number && number.isValid()) ? this.setState({ showSMSCheckbox: true }) : this.setState({ showSMSCheckbox: false });

    return number;
  }

  phoneNumberValidator = (phoneNumber) => {
    if (!phoneNumber) {
      this.setState({ showSMSCheckbox: false });
      return;
    }
    const number = this.parsePhoneNumber(phoneNumber);
    if (!number || !number.isValid()) return 'Not a valid phone number';
  }

  blurName = (_, name) => {
    if (!name) return;
    const [firstName, ...lastNames] = this.parseFullName(name);
    this.trackEvent('user_name_app', {
      firstName,
      lastName: lastNames.join(' '),
    });
  };

  blurEmail = (ev, email) => {
    this.props.checkEmailExists(ev, email);
    if (!this.validateEmail(email)) {
      this.trackEvent('user_email_app', { email });
    }
    this.setState({ emailFocus: false });
  };

  changeBirthday = (_, birthday) => {
    if (!birthday) return;
    this.trackEvent('user_birthday_app', {
      birthday: birthday.toISOString().substr(0, 10)
    });
  }

  changeCountry = (countryHint) => {
    this.setState({ countryHint: countryHint });
    window.analytics.track('user_country_app', {
      countryHint
    });
  }

  submitForm = async (values) => {
    if (!values.email) {
      throw new SubmissionError({
        email: 'Email field required.'
      });
    }
    const age = calculateAge(values.birthday);
    if (age < 13) throw new SubmissionError({ _error: UNDER_LEGAL_AGE_ERROR });

    const [firstName, ...lastNames] = this.parseFullName(values.fullName);

    try {
      const lastName = lastNames.join(' ');
      const birthday = values.birthday.toISOString().substr(0, 10);
      let phoneNumber;
      let countryCode;
      if (this.state.countryHint === 'US') {
        const phone = values.phoneNumber && this.parsePhoneNumber(values.phoneNumber);
        if (phone) {
          phoneNumber = phone.nationalNumber;
          countryCode = phone.countryCallingCode;
        }
      }

      this.trackEvent('user_register_app', {
        firstName,
        lastName,
        email: values.email,
        birthday,
        timestamp: new Date().toISOString(),
      });

      await this.props.createUser({
        email: values.email,
        firstName,
        lastName,
        password: values.password,
        birthday,
        gender: this.props.facebookInfo.gender,
        wantsMarketingEmails: values.wantsMarketingEmails,
        wantsMarketingSMS: values.wantsMarketingSMS,
        facebookToken: this.props.facebookInfo.accessToken,
        country: this.state.countryHint,
        phoneNumber,
        countryCode
      });
    } catch (err) {
      if (err instanceof EmailTakenError) {
        throw new SubmissionError({ email: err.message });
      } else if (err instanceof FacebookUserAlreadyExists) {
        this.setState({ facebookError: err.message });
      }

      throw new SubmissionError({ _error: err.message });
    }

    this.props.onSuccessfulSubmit();
  }

  showMenuOptions = (bool) => {
    this.setState({ isSelectMenuOpen: bool });
  }

  handleSubmit = this.props.handleSubmit(this.submitForm)

  handleWolfBack = () => {
    this.props.closeWebview();
  }

  openTermsOfUse = () => {
    this.props.openTermsOfUse();
  }

  openPrivacyPolicy = () => {
    this.props.openPrivacyPolicy();
  }

  redirectToLogin = () => {
    this.props.redirectToLogin();
  }

  openCoppaHelp = () => {
    this.props.openCoppaHelp();
  }

  emailWarning = () => {
    return Boolean(this.props.emailWarning) || this.state.emailFocus;
  }

  emailHasFocus = () => {
    this.setState({ emailFocus: true });
    this.props.clearEmailWarning();
  }

  render() {
    return (
      <div className={ classNames('create-account-info', this.props.mediaType) }>
        { this.props.appName === 'wolf'
          ? <FloatingBar
            backFunction={ this.handleWolfBack }
            jumpFunction={ this.redirectToLogin }
            skipNavigation={ true }
            showLoginButton={ true }
            appName={ this.props.appName }
          />
          : <FloatingBar showLoginButton={ true } />
        }
        { this.props.error === UNDER_LEGAL_AGE_ERROR
          ? <UnderAgeWarningModal
            title="Sorry!"
            content="You must be 13 years or older to use iFit. If you entered your birthday incorrectly, click the Help button below."
            primaryActionLabel="Help"
            handlePrimaryActionClick={ this.openCoppaHelp }
            openDefault={ true }
          />
          : null }
        <form onSubmit={ this.handleSubmit }>
          <div className="login-options">
            <button
              className="login-button facebook-button"
              onClick={ this.getUserFacebookInfo }
            >
              <Icon
                name={ NAMES.FACEBOOK_BOXED }
                color={ scienceBlue }
                size={ 24 }
                unitSize="px"
              />
          Sign up with Facebook
            </button>
            { this.state.facebookError &&
          <p className="error-text" style={ styles.errorStyle }>{ this.state.facebookError }</p>
            }
            <div className="or-container">
              <p>or</p>
            </div>
          </div>
          <EmailField
            validate={ this.validateEmail }
            errorText={ this.props.emailWarning }
            onBlur={ this.blurEmail }
            onFocus={ this.emailHasFocus }
          />
          <FullNameField validate={ this.validateFullName } onBlur={ this.blurName } />
          <PasswordField validate={ this.validatePassword } showPassword={ this.props.showPassword } />
          <CountryField
            onChange={ this.changeCountry }
            isOpen={ this.state.isSelectMenuOpen }
            open={ this.showMenuOptions }
            value={ this.state.countryHint }
          />
          {(!this.state.countryHint || this.state.countryHint === 'US') &&
            <PhoneField validate={ this.phoneNumberValidator } countryHint={ this.state.countryHint } />
          }
          <BirthdayField className="birthday-field" validate={ this.validateBirthday } onChange={ this.changeBirthday } />
          <MarketingEmailsField />
          { this.state.showSMSCheckbox && this.state.countryHint === 'US' && <MarketingSMSField /> }
          <TermsOfServiceField tosClick={ openTermsOfUse } privacyClick={ openPrivacyPolicy } validate={ this.validateTermsOfService } />
          { this.props.error && <p className="submission-error" style={ styles.errorStyle }>{ this.props.error }</p> }
          <MuiRaisedButton
            className="account-button"
            type="submit"
            label={ this.props.submitting ? 'Submitting...' : 'Create Account' }
            primary={ true }
            fullWidth={ true }
            style={ submitButtonStyle.button }
            labelStyle={ submitButtonStyle.label }
            disabled={ this.props.invalid || this.props.submitting || this.emailWarning() }
          />
        </form>
      </div>
    );
  }
}

let CreateAccountLoginInfoPage = reduxForm({
  form: 'createAccountLoginInfo',
  enableReinitialize: true,
})(PureCreateAccountLoginInfoPage);

CreateAccountLoginInfoPage = connect(
  state => ({
    facebookInfo: state.facebook,
    mediaType: state.config.mediaType,
    appName: state.config.appName,
    emailWarning: state.user.emailWarning,
    initialValues: {
      email: state.user.email || state.facebook.email,
      birthday: state.facebook.birthday ? new Date(state.facebook.birthday) : null,
      fullName: state.facebook.name,
      country: 'US'
    }
  }),
  dispatch => ({
    createUser: user => dispatch(createUser(user)),
    onSuccessfulSubmit: () => dispatch(Navigation.forward()),
    checkEmailExists: (ev, email) => {
      if (email) dispatch(checkEmailExists(email));
      return '';
    },
    clearEmailWarning: () => dispatch(updateEmailWarning()),
    getUserFacebookProfile: async () => {
      const fbData = await dispatch(getFacebookProfileData());
      const fbProfileData = fbData.value;

      dispatch(batchActions([
        changeInput('createAccountLoginInfo', 'email', fbProfileData.email),
        changeInput('createAccountLoginInfo', 'fullName', fbProfileData.name),
        changeInput('createAccountLoginInfo', 'birthday', fbProfileData.birthday ? new Date(fbProfileData.birthday) : null),
      ]));
    },
    closeWebview: () => dispatch(closeWebview()),
    openTermsOfUse: () => dispatch(openTermsOfUse()),
    openPrivacyPolicy: () => dispatch(openPrivacyPolicy()),
    redirectToLogin: () => dispatch(redirectToLogin()),
    openCoppaHelp: () => dispatch(openCoppaHelp())
  })
)(CreateAccountLoginInfoPage);

export default CreateAccountLoginInfoPage;
